import { SoChipType } from "@/types/components/SoChip"
import { ITariff, IProductDetails } from "./tariff.types"
import tariffHelper from "@/helpers/tariffHelper"
import { formatPriceInPence } from "@/helpers/moneyHelpers"
import { SoChipProps } from "@/components/SoChip.vue"
import {
  SO_FLEX_TARIFF_CODE,
  SO_FLEX_GREEN_TARIFF_CODE,
  SO_GREEN_TRACKER_TARIFF_CODE,
} from "@/constants/tariff"
import { Dayjs } from "dayjs"

export class Tariff implements ITariff {
  id: number
  code: string
  gsp: string
  display: string
  productDetails: IProductDetails
  isPrepay: boolean
  isSmartPrepay: boolean
  isAvailableToPublic: boolean
  directDebit: boolean
  durationMonths: number | null
  createdAt: Dayjs

  constructor({
    id,
    code,
    gsp,
    display,
    productDetails,
    isPrepay,
    isSmartPrepay,
    isAvailableToPublic,
    directDebit,
    durationMonths,
    createdAt,
  }: {
    id: number
    code: string
    gsp: string
    display: string
    productDetails: IProductDetails
    isPrepay: boolean
    isSmartPrepay: boolean
    isAvailableToPublic: boolean
    directDebit: boolean
    durationMonths: number | null
    createdAt: Dayjs
  }) {
    this.id = id
    this.code = code
    this.gsp = gsp
    this.display = display
    this.productDetails = productDetails
    this.isPrepay = isPrepay
    this.isSmartPrepay = isSmartPrepay
    this.isAvailableToPublic = isAvailableToPublic
    this.directDebit = directDebit
    this.durationMonths = durationMonths
    this.createdAt = createdAt
  }

  get description(): string | undefined {
    if (this.code.includes(SO_FLEX_GREEN_TARIFF_CODE))
      return "This is a green tracker tariff with 100% renewable electricity. The tariff tracks the level of Ofgem's price cap, but customers pay 1.5p/kWh extra to cover the 100% renewable electricity. The tariff lasts 12 months. Payment is by Direct Debit, and there are no exit fees."
    if (this.code.includes(SO_GREEN_TRACKER_TARIFF_CODE)) {
      return `This is a ${this.durationMonths}-month green tracker tariff with 100% renewable electricity. The tariff is priced below Ofgem's price cap by an average of £50 a year (£25 per fuel). Tracker tariff rates reflect current prices. These are reviewed every three months and can go up or down with Ofgem's price cap. The customer needs to have a smart meter or agree to get one installed within 90 days, and must be on Direct Debit.`
    }
    return undefined
  }

  get bundleCode(): string {
    return tariffHelper.getBundleCode(this.code)
  }

  get isGas(): boolean {
    return tariffHelper.isGas(this.code)
  }

  get unitRatesFormatted(): string {
    if (this.productDetails.unitRate) {
      return `${formatPriceInPence(this.productDetails.unitRate)}/kWh`
    }

    const unitRates = this.productDetails.unitRates || {}
    const keys = Object.keys(unitRates)
    const multipleRates = keys.length > 1

    return keys
      .map((key) => {
        const rateValue = unitRates[key]
        return multipleRates
          ? `(${key}) ${formatPriceInPence(rateValue)}/kWh`
          : `${formatPriceInPence(rateValue)}/kWh`
      })
      .join(" ")
  }

  get isExport(): boolean {
    return this.display.toLowerCase().includes("export")
  }

  get isExclusive(): boolean {
    return this.code.includes("-X-")
  }

  get isFutureStartDateTariff(): boolean {
    return tariffHelper.isFutureStartDateTariff(this.productDetails)
  }

  get isNSC(): boolean {
    return this.code.includes("NSC")
  }

  get isVariable(): boolean {
    return (
      this.code.includes(SO_FLEX_TARIFF_CODE) ||
      this.code.includes(SO_FLEX_GREEN_TARIFF_CODE) ||
      this.code.includes(SO_GREEN_TRACKER_TARIFF_CODE)
    )
  }

  get isOneRate(): boolean {
    return this.productDetails.tariffComparisonRateName.includes("1R")
  }

  get tariffChips(): SoChipProps[] {
    const chips = [] as SoChipProps[]

    const addChip = (condition, text, type) => {
      if (condition) {
        chips.push({
          text: text,
          type: type,
        })
      }
    }

    addChip(this.isFutureStartDateTariff, "FSD", SoChipType.IMPORTANT)
    addChip(this.isNSC, "NSC", SoChipType.IMPORTANT)

    return chips
  }

  get tariffTypeChips(): SoChipProps[] {
    return [
      this.isVariable
        ? { text: "Variable", type: SoChipType.WARNING }
        : { text: "Fixed", type: SoChipType.SUCCESS },
    ]
  }
}
